import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
// @ts-ignore: allow side-effect CSS import without module declarations
import "react-toastify/dist/ReactToastify.css";
import LoadingScreen from "../../../../shared/components/LoadingScreen";
import OrdersToolbar from "./OrdersToolbar";
import DeleteDialog from "./DeleteDialog";
import CommonTable, { HeadCell } from "../../../grocery/components/CommonTable";
import { useOrders } from "../../../../shared/hooks/useOrders";
import useImport from "./useImport";
import useExport from "./useExport";
import { Box, IconButton, TablePagination, Tooltip } from "@mui/material";
import ViewListIcon from "@mui/icons-material/ViewList";
import DeleteIcon from "@mui/icons-material/Delete";
import { OrderTableRow } from "../../../../shared/services/interfacev2";
import { DEFAULT_ROWS_PER_PAGE_OPTIONS } from "src/business-types/grocery/hooks/tableConfig";
import { useUser } from "src/shared/context/UserContext";
import { pageTitle } from "src/shared/utils/brand";
import { useUserTheme } from "src/shared/hooks/useUserTheme";
import { Button } from "src/business-types/grocery/components/Button";

export const formatNumber = (value: number) => {
  return value < 1000 ? value.toString().padStart(4, "0") : value.toString();
};

const OrderSearch: React.FC = () => {
  const navigation = useNavigate();
  const [, setBusinessType] = useState<string>("");
  const [isEmployee, setIsEmployee] = useState(false);
  const [openDialog, setOpenDialog] = useState(false);
  const [selectedOrderId, setSelectedOrderId] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);

  const { userConfigration, user } = useUser();
  const { inputRef, handleImport } = useImport({ setLoading });
  const { handleExport } = useExport({ setLoading });
  const { sidebarThemeClass } = useUserTheme();

  const {
    orders,
    loadings,
    page,
    rowsPerPage,
    paginationData,
    searchTerm,
    selectedOption,
    selectedDate,
    selectedDate2,
    handleSearch,
    handleFilterChange,
    applyCustomDateFilter,
    setSelectedDate,
    setSelectedDate2,
    handleDeleteOrder,
    handleChangePage,
    handleChangeRowsPerPage,
    getDateRangeForExport,
  } = useOrders({});

  useEffect(() => {
    document.title = pageTitle("Orders");
    setBusinessType(user?.businessType ?? "");
    setIsEmployee(!!sessionStorage.getItem("employee"));
  }, []);

  const handleViewOrder = (orderId: string) => {
    navigation(isEmployee ? `/v1/employee/order-details/${orderId}` : `/v1/order-details/${orderId}`);
  };

  const handleOpenDialog = (orderId: string) => {
    setSelectedOrderId(orderId);
    setOpenDialog(true);
  };

  const handleCloseDialog = () => {
    setOpenDialog(false);
    setSelectedOrderId(null);
  };

  const handleConfirmDelete = () => {
    if (selectedOrderId !== null) handleDeleteOrder(selectedOrderId);
    handleCloseDialog();
  };

  const toBoolean = (value: any) => value === true || value === "true";

  const billNoEnabled = toBoolean(userConfigration?.billNoEnabled);
  const orderIdEnabled = toBoolean(userConfigration?.orderIdEnabled);

  // ✅ Dynamic Head Cells
  const orderHeadCells: HeadCell<OrderTableRow>[] = [
    { id: "S.No" as const, label: "S.No" },
    { id: "Name" as const, label: "Name" },
    { id: "Phone number" as const, label: "Phone number" },
  ];

  if (orderIdEnabled) {
    orderHeadCells.push({ id: "Order ID" as const, label: "Order ID" });
  }

  if (billNoEnabled) {
    orderHeadCells.push({ id: "BillNo" as const, label: "Bill No." });
  }

  orderHeadCells.push({ id: "Order status" as const, label: "Order status" }, { id: "Actions" as const, label: "Actions" });

  // ✅ Transform Orders Safely
  const transformedOrders: OrderTableRow[] = orders
    .filter((order) => order?.orders?.length && order.orders[0]?.status !== "deleted")
    .map((order, index) => {
      const baseOrder = order.orders?.[0];
      if (!baseOrder) return null as any;

      const row: any = {
        id: baseOrder.orderId,
        "S.No": index + 1 + page * rowsPerPage,
        Name: [order.customerDetails?.firstName, order.customerDetails?.middleName, order.customerDetails?.lastName].filter(Boolean).join(" "),
        "Phone number": String(order.customerDetails?.phones?.[0]?.phone ?? "N/A"),
        "Order status": baseOrder.status,

        Actions: (
          <Box display="flex" alignItems="center" gap={1}>
            <Tooltip title="View order">
              <IconButton onClick={() => handleViewOrder(String(baseOrder.orderId))}>
                <ViewListIcon sx={{ color: "#000" }} />
              </IconButton>
            </Tooltip>

            {baseOrder.status === "Cancelled" && (
              <Tooltip title="Delete order">
                <IconButton onClick={() => handleOpenDialog(String(baseOrder.orderId))}>
                  <DeleteIcon sx={{ color: "#d32f2f" }} />
                </IconButton>
              </Tooltip>
            )}
          </Box>
        ),
      };

      // ✅ Conditional fields
      if (orderIdEnabled) {
        row["Order ID"] = String(baseOrder.orderId);
      }

      if (billNoEnabled) {
        row["BillNo"] = baseOrder.billNumber ? formatNumber(baseOrder.billNumber) : "N/A";
      }

      return row;
    })
    .filter(Boolean);

  return (
    <div className={`flex flex-col h-full w-full overflow-hidden p-1.5`}>
      {/* <PageTitle title="- Orders" /> */}
      <div className={`w-full h-full flex flex-col items-center shadow-inner rounded-lg overflow-auto ${sidebarThemeClass}`}>
        <OrdersToolbar
          searchTerm={searchTerm}
          handleSearch={handleSearch}
          inputRef={inputRef}
          handleImport={handleImport}
          handleExport={(type) =>
            handleExport(
              type,
              orders.map((order) => ({
                ...order,
                Orderid: order.orders?.[0]?.orderId,
              })),
              selectedOption,
              searchTerm,
              page,
              rowsPerPage,
              getDateRangeForExport,
              selectedDate,
              selectedDate2,
            )
          }
          orders={orders}
          selectedDate={selectedDate}
          selectedDate2={selectedDate2}
          selectedOption={selectedOption}
          handleFilterChange={handleFilterChange}
        />

        {selectedOption === "CustomizeDate" && (
          <div className="flex items-center gap-3 flex-wrap px-4 py-2">
            <div className="flex items-center gap-2">
              <label className="text-sm font-medium">From</label>
              <input type="date" value={selectedDate} onChange={(e) => setSelectedDate(e.target.value)} className="border rounded-md px-2 py-1" />
            </div>

            <div className="flex items-center gap-2">
              <label className="text-sm font-medium">To</label>
              <input type="date" value={selectedDate2} onChange={(e) => setSelectedDate2(e.target.value)} className="border rounded-md px-2 py-1" />
            </div>

            <Button label="Apply" onClick={applyCustomDateFilter} />
          </div>
        )}

        <div className="w-full overflow-y-auto">
          <CommonTable<OrderTableRow>
            data={transformedOrders}
            headCells={orderHeadCells}
            title=""
            showToolbar={false}
            selectable={false}
            denseToggle={false}
            pageSize={rowsPerPage}
            pagenation={false}
            showFilterIcon={false}
            className="shadow-inner rounded-lg bg-white p-2"
          />

          <div className="flex justify-end mr-8 mt-2">
            <TablePagination
              component="div"
              count={paginationData.totalRecords}
              page={page}
              onPageChange={handleChangePage}
              rowsPerPage={rowsPerPage}
              onRowsPerPageChange={handleChangeRowsPerPage}
              rowsPerPageOptions={DEFAULT_ROWS_PER_PAGE_OPTIONS}
            />
          </div>
        </div>

        <DeleteDialog open={openDialog} onClose={handleCloseDialog} onConfirm={handleConfirmDelete} />
      </div>

      {(loading || loadings) && <LoadingScreen />}
    </div>
  );
};

export default OrderSearch;
